import { type AppTrainingWorkflow, isFlowWorkflow } from '../../../app-core/lib/workflow/Workflow'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import { flatten } from '../../../shared/lib/array.no-deps'
import { uniq } from '../../../shared/lib/functional/array.no-deps'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'
import { isNotNil } from '../../../shared/lib/functional/nil.no-deps'
import { sortAlphabetical } from '../../../shared/lib/string.no-deps'
import { I18N_replaceNameSlug } from '../I18N/I18N.slugs'
import { IDB_trainingCompletions } from '../IDB/IDB.dexie'
import { IDB_workflowReadSome } from '../IDB/IDB.table.workflow'
import { WF_fetchByIds } from './WF.fetch'

export function WF_fetchMissingTrainingRecommendations(
    clientLogger: ClientLogger,
    taskId: number
): Promise<AppTrainingWorkflow[]> {
    return WF_fetchMissingTrainingRecommendationsBulk(clientLogger, [taskId]).then((map) => map.get(taskId))
}

export async function WF_fetchMissingTrainingRecommendationsBulk(
    clientLogger: ClientLogger,
    taskIds: number[]
): Promise<Map<number, AppTrainingWorkflow[]>> {
    const taskTrainingRecommendationIds = await fetchTaskTrainingRecommendationIds(taskIds)

    // Fetch a list of missing training IDs using all training recommendations linked to given tasks.
    const uniqueTrainingRecommendationIdSet = new Set(flatten([...taskTrainingRecommendationIds.values()]))
    const missingTrainingIds = await fetchMissingTrainingIds([...uniqueTrainingRecommendationIdSet])
    const missingTrainings = await fetchTrainingWorkflows(clientLogger, missingTrainingIds)

    // Create a map where key is task ID and value is a list of missing training recommendations.
    const taskMissingTrainingRecommendations = new Map<number, AppTrainingWorkflow[]>()
    for (const [taskId, trainingIds] of taskTrainingRecommendationIds.entries()) {
        const missingWorkflowTrainings = trainingIds.reduce<AppTrainingWorkflow[]>((acc, trainingId) => {
            const missingTraining = missingTrainings.get(trainingId)
            if (missingTraining) {
                acc.push(missingTraining)
            }
            return acc
        }, [])

        // Training recommendations need to be sorted by name.
        taskMissingTrainingRecommendations.set(
            taskId,
            sortAlphabetical(missingWorkflowTrainings, (training) => training.name)
        )
    }

    return taskMissingTrainingRecommendations
}

async function fetchTaskTrainingRecommendationIds(taskIds: number[]): Promise<Map<number, number[]>> {
    const tasks = await IDB_workflowReadSome(taskIds).then((workflows) =>
        workflows.filter((workflow) => workflow && !workflow.isDeleted).map(I18N_replaceNameSlug)
    )

    return new Map(
        tasks.map((task) => [task.id, isFlowWorkflow(task) ? (pipe(task.trainingRecommendations, uniq) ?? []) : []])
    )
}

async function fetchMissingTrainingIds(trainingIds: number[]): Promise<number[]> {
    const completedTrainingIds = await IDB_trainingCompletions()
        .where('id')
        .anyOf(trainingIds)
        .filter((item) => item.isComplete)
        .toArray()
        .then((items) => new Set(items.map((item) => item.id)))

    return trainingIds.filter((trainingId) => !completedTrainingIds.has(trainingId))
}

async function fetchTrainingWorkflows(
    clientLogger: ClientLogger,
    trainingIds: number[]
): Promise<Map<number, AppTrainingWorkflow>> {
    const workflows = await WF_fetchByIds(clientLogger, trainingIds, false)
    const trainingWorkflows = workflows.filter(isNotNil).filter(isFlowWorkflow)

    return new Map(trainingWorkflows.map((training) => [training.id, training]))
}
